/**
 * dosyaTakipYardimci.js — pure helpers behind the file tracking lists of the stand detail's "Proje Bilgileri" tab
 * (backlog #211): which uploaded file stands for a title or a photo corner, who reviewed it, which
 * revisions are still open. No React, no services - tested in dosyaTakipYardimci.test.js.
 *
 * Data comes from the project (GET /projects/by-stand): `uploadedFiles[]` {id, standFileId, subStandFileId,
 * originalFileName, approvalStatus 0/1/2, uploadedAt, reviewedBy, reviewedAt, rejectReason, deleted},
 * `fileApprovals[]` {standFileId, status, approvedBy, updatedAt} and `revisions[]`. The reviewer is a user ID
 * only; the names come from GET /users/staff (see personLabel).
 */
import { currentLocaleTag } from "../../i18n";

export function timeOf(value) {
  const t = value == null ? NaN : new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Date for the lists; "—" without one. */
export function fmtTarih(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return "—";
  return d.toLocaleDateString(currentLocaleTag(), { day: "2-digit", month: "short", year: "numeric" });
}

/** Not-deleted files, newest upload first (a tie keeps the later row first: the backend appends). */
export function filesNewestFirst(files) {
  return (files || [])
    .filter((f) => f && !f.deleted)
    .map((f, i) => ({ f, i }))
    .sort((a, b) => timeOf(b.f.uploadedAt) - timeOf(a.f.uploadedAt) || b.i - a.i)
    .map((x) => x.f);
}

/** The file that stands for a slot: the newest upload that is not deleted, or null. */
export function latestFile(files) {
  return filesNewestFirst(files)[0] ?? null;
}

/** Files of one title (standFileId) - every version, newest first. */
export function filesOfTitle(uploadedFiles, standFileId) {
  return filesNewestFirst((uploadedFiles || []).filter((f) => Number(f.standFileId) === Number(standFileId)));
}

/**
 * One line per photo corner (angle) plus a line per file that matches no corner. `angles` are
 * {subStandFileId, label}; a corner with no file gives { file: null } (= "not uploaded").
 */
export function angleEntries(titleFiles, angles) {
  const list = angles || [];
  const entries = list.map((a) => ({
    key: `a-${a.subStandFileId}`,
    label: a.label,
    file: latestFile((titleFiles || []).filter((f) => Number(f.subStandFileId) === Number(a.subStandFileId))),
  }));
  const known = new Set(list.map((a) => Number(a.subStandFileId)));
  filesNewestFirst((titleFiles || []).filter((f) => !known.has(Number(f.subStandFileId)))).forEach((f) => {
    entries.push({ key: `f-${f.id}`, label: null, file: f });
  });
  return entries;
}

/** One line per version of a title's file (no corners), newest first. */
export function versionEntries(titleFiles) {
  return filesNewestFirst(titleFiles).map((f) => ({ key: `f-${f.id}`, label: null, file: f }));
}

/** The title-level decision (project.fileApprovals): { status, by, at } once somebody decided, else null. */
export function titleDecision(fileApprovals, standFileId) {
  const fa = (fileApprovals || []).find((x) => Number(x.standFileId) === Number(standFileId));
  if (!fa) return null;
  const status = Number(fa.status ?? 0);
  if (status === 0 && fa.approvedBy == null && !fa.updatedAt) return null;
  return { status, by: fa.approvedBy ?? null, at: fa.updatedAt ?? null };
}

/** Unfinished revisions that target a document title, newest first. `isUnfinished` is projectService.isRevisionUnfinished. */
export function openDocumentRevisions(revisions, standFileId, isUnfinished) {
  return (revisions || [])
    .filter((r) => r.targetKind === "DOCUMENT" && Number(r.standFileId) === Number(standFileId) && isUnfinished(r))
    .sort((a, b) => timeOf(b.updatedAt) - timeOf(a.updatedAt));
}

/** "Ad Soyad" of a staff user row from GET /users/staff. */
export function staffName(u) {
  return [u?.userName, u?.userSurname].filter(Boolean).join(" ").trim() || u?.userLoginName || null;
}

/** id -> name map from the staff list. */
export function staffNameMap(rows) {
  const map = {};
  (rows || []).forEach((u) => {
    const n = staffName(u);
    if (u && u.id != null && n) map[u.id] = n;
  });
  return map;
}

/** The reviewer shown: the name when known, "#id" when the staff list did not have it, null without an id. */
export function personLabel(id, names) {
  if (id == null) return null;
  return names?.[id] ?? `#${id}`;
}

/** Does any file or title decision of the project name a reviewer (= the staff names are worth fetching)? */
export function needsReviewerNames(project) {
  if (!project) return false;
  return (project.uploadedFiles || []).some((f) => f.reviewedBy != null)
    || (project.fileApprovals || []).some((fa) => fa.approvedBy != null);
}
